<template>
  <RouterView v-slot="{ Component }">
    <template v-if="Component">
      <component :is="Component" />
    </template>

    <template v-else>
      <p class="mpf-panel__body" v-if="actionResponseTime">
        We aim to respond to requests in {{ formatHoursToReadableTime(actionResponseTime) }},
        usually by email or text.
      </p>
      <p class="mpf-panel__body" v-else>
        We aim to respond to requests as soon as possible, usually by email or text.
      </p>
      <p>If your request is an emergency or you're not sure:</p>
      <ul class="mpf-u-margin-bottom-6">
        <li v-if="tenant?.localOrganisation.phone">
          call the surgery on
          <!-- Phone needs +44 or relevant country code -->
          <a href="tel:{{ tenant?.localOrganisation.phone }}" class="mpf-link">{{
            tenant?.localOrganisation.phone
          }}</a>
        </li>

        <li v-if="isNhsJurisdiction">
          if the surgery is closed, phone <a href="tel:111">111</a> or go online to
          <a href="https://111.nhs.uk/" target="_blank" rel="noopener noreferrer" class="mpf-link"
            >Get help for your symptoms - NHS 111</a
          >
        </li>
        <li>in an emergency, phone 999 or go to A&amp;E</li>
      </ul>

      <MpfCareCard
        variant="emergency"
        heading="Phone 999 or go to A&amp;E now if you have:"
        headingLevel="2"
      >
        <!-- only use slot if tenant loaded -->
        <template v-if="tenant?.localOrganisation?.phone || tenant?.localOrganisation?.email">
          <ul>
            <li>
              signs of a heart attack (chest pain, pressure, heaviness or tightness across the
              chest)
            </li>
            <li>
              signs of a stroke (face dropping on one side, cannot hold both arms up, difficulty
              speaking)
            </li>
            <li>sudden confusion (unsure of own name or age)</li>
            <li>suicide attempt (by taking something or self-harming)</li>
            <li>
              severe difficulty breathing (not being able to get words out, choking or gasping)
            </li>
            <li>a serious injury (e.g. after a serious accident)</li>
            <li>heavy bleeding (spraying, pouring or enough to make a puddle)</li>
            <li>severe injuries (after a serious accident or assault)</li>
            <li>a seizure or fit (shaking, jerking, or unconscious &amp; can’t be woken up)</li>
            <li>sudden, rapid swelling (of the lips, mouth, throat or tongue)</li>
            <li>
              labour or child birth (waters breaking, more frequent intense cramps (contractions),
              baby coming, or just born)
            </li>
          </ul>
        </template>
      </MpfCareCard>
      <MpfButton label="My request is not an emergency" @click="handleClick" />
    </template>
  </RouterView>
</template>

<script setup lang="ts">
  import { RouterView, useRoute } from 'vue-router';
  import { useUrlSegments } from '../composables/useUrlSegments';
  import MpfCareCard from '../components/MpfCareCard/MpfCareCard.vue';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import { storeToRefs } from 'pinia';
  import { useMedicalRequestStore } from '../../stores/useMedicalRequestStore';
  import { router } from '../../router';
  import { useAdminRequestStore } from '../../stores/useAdminRequestStore';
  import { useTenantStore } from '../../stores/useTenantStore';

  import { useRouteMetaOverrideStore } from '../../stores/useRouteMetaOverrideStore';
  import { onMounted, onUnmounted, watch } from 'vue';
  import { formatHoursToReadableTime } from '../helpers/dates';
  import { RequestType } from '@/types';
  import { useRequestFormAvailability } from '@/lib/composables/useRequestFormAvailability';

  const { firstSegment, lastSegment } = useUrlSegments();
  const { check } = useRequestFormAvailability();
  const medicalRequestStore = useMedicalRequestStore();
  const adminRequestStore = useAdminRequestStore();

  const tenantStore = useTenantStore();

  const { tenant, actionResponseTime, isNhsJurisdiction, informationBanner } =
    storeToRefs(tenantStore);

  tenantStore.setActionResponseTime(firstSegment.value);
  const route = useRoute();

  const metaOverride = useRouteMetaOverrideStore();

  onMounted(async () => {
    // NB check if form open on page load too
    await check(firstSegment.value as RequestType);
  });

  const handleClick = async () => {
    await check(firstSegment.value as RequestType);
    if (firstSegment.value === 'repeat-prescription-request') {
      // NB. Prescriptions do not need a warning step
      await router.push(`${firstSegment.value}/prescriptions-list`);
    } else if (firstSegment.value === 'admin-request') {
      await router.push(
        `/${firstSegment.value}/${adminRequestStore.isNhsVerified ? 'admin-problem-details' : 'admin-problem-details'}`,
      );
    } else {
      await router.push(
        `/${firstSegment.value}/${medicalRequestStore.isNhsVerified ? 'problem-details' : 'problem-details'}`,
      );
    }
  };

  //TODO this watch needs to be used due to some tech debt. The MedicalWarningVue page acts as a parent to all of the
  // other pages in the form so we need to use a watch instead of onMounted.
  watch(
    () => route.fullPath,
    () => {
      metaOverride.clearOverrides();
      if (lastSegment.value === 'medical-warning') {
        tenantStore.setInformationBanner(firstSegment.value);
        if (informationBanner) {
          metaOverride.notificationBanner = {
            title: 'Information',
            text: informationBanner.value,
          };
        }
      }
    },
  );

  onUnmounted(() => {
    metaOverride.clearNotificationBanner();
  });
</script>
